import _set from 'lodash/set';
import { IShapeBaseModel, IShapeModel } from '../element-modeles/Interface/shape-model.interface';
import { IBaseModel } from '../element-modeles/Interface/base/base-model.interface';
import ModelModificationStash from './model-modification-stash';
import TextAdjustEffectManager from './text-adjust-effect-manager';
import { transaction } from '@src/slide/domain/slide-app';
import { ISlideBaseModel } from '../element-modeles/Interface/slide-model.interface';
import { isShapeModel } from '../elements/element-judge';

/**
 * 处理数据之间的联动
 */
class LinkageChangeHelper {
    /**
     * 处理联动场景的统一入口
     * @param uiData 改动的ui数据
     * @param uiModel uiModel
     */
    public static checkAndHandleLinkageChange(uiData: Partial<IBaseModel>, uiModel: IBaseModel) {
        // 如果当前变更已经处理过，直接返回，避免产生死循环
        if (ModelModificationStash.getInstance().hasModification(uiData, uiModel.key)) {
            return;
        }
        // 记录下当前变更
        const stashedModification = ModelModificationStash.getInstance().stashModification(uiData, uiModel.key);
        // 处理连锁反应时需要使用当前所有准备修改数据进行计算，否则可能会使用过时的数据计算得到错误的结果
        LinkageChangeHelper.handleLinkageChange(stashedModification, uiModel);
        LinkageChangeHelper.clearModelModificationAsync(uiModel);
    }

    private static handleShapeChange(uiData: Partial<IShapeModel>, shapeModel: IShapeModel) {
        TextAdjustEffectManager.checkAndRecalculateTextAdjustEffect(shapeModel, uiData);
    }

    private static handleLinkageChange(uiData: Partial<IBaseModel>, uiModel: IBaseModel): void {
        if (isShapeModel(uiModel)) {
            LinkageChangeHelper.handleShapeChange(uiData as Partial<IShapeModel>, uiModel);
        }
        return;
    }

    /**
     * 兜底逻辑：放在异步中清除当前 model 要更新的数据缓存，避免某些场景下 sdkOnDidChange 没触发导致当前的数据变更影响到下一次数据变更的计算
     * ModelModificationStash 中管理的都是当前同步任务中即将要变更的数据，对下次同步任务的数据变更计算没有意义
     * @param uiModel
     */
    private static clearModelModificationAsync(uiModel: IBaseModel) {
        Promise.resolve().then(() => {
            ModelModificationStash.getInstance().clearModification(uiModel.key);
        });
    }
}
// 描述属性变更类型
export enum PropertyModificationType {
    update = 'update',
    remove = 'remove'
}
// 描述属性变更情况，第一个为变更属性名，第二个为变更类型
type ModelPropertyDescription = [keyPath: string, type: PropertyModificationType];

/**
 * 给 shapeModel 中的方法创建处理联动的装饰器
 * 目标方法的参数需要是要变更的属性值
 * @param modelPropertyDescriptions 是一个数组，索引为 i 的元素是描述目标方法中索引为 i 的参数所代表的属性变更情况
 * @returns
 */
export function linkageDecoratorFactoryForShapeModel(modelPropertyDescriptions: ModelPropertyDescription[]) {
    return function (target: IShapeBaseModel, name: string, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            // 生成变更的数据
            const newUiData = {};
            modelPropertyDescriptions.forEach(([keyPath, type], index) => {
                const propertyValue = args[index];
                if (type === PropertyModificationType.update) {
                    _set(newUiData, keyPath, propertyValue);
                    return;
                }
                if (type === PropertyModificationType.remove) {
                    _set(newUiData, keyPath, undefined);
                }
            });
            let result;
            // 把原始的函数逻辑和执行联动的逻辑放在一个 transaction 中执行，保证 undo redo 只会新增一个变更
            transaction(() => {
                result = originalMethod.apply(this, args);
                LinkageChangeHelper.checkAndHandleLinkageChange(newUiData, this);
            });
            return result;
        };
        return descriptor;
    };
}

/**
 * 给 slideModel 中的方法创建处理联动的装饰器
 * 目前联动逻辑处理的都是 shape 相关的，所以目标方法都是和 shape 元素相关，后续拓展新的联动时，可以再扩展这个工厂的逻辑
 * @param elementsIndex 标记目标方法中 elements 参数所在的索引
 * @param modelPropertyDescriptions 是一个数组，索引为 i 的元素是描述目标方法中索引为 i 的参数所代表的属性变更情况
 * @returns
 */
export function linkageDecoratorFactoryForSlideModel(
    elementsIndex: number,
    modelPropertyDescriptions: ModelPropertyDescription[],
) {
    return function (target: ISlideBaseModel, name: string, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            let result;
            const elements = args[elementsIndex] || [];
            // 把原始的函数逻辑和执行联动的逻辑放在一个 transaction 中执行，保证 undo redo 只会新增一个变更
            transaction(() => {
                result = originalMethod.apply(this, args);
                // 每个子元素单独处理自己的联动
                elements.forEach((element, elementIndex: number) => {
                    const newUiData = {};
                    modelPropertyDescriptions.forEach(([keyPath, type], index) => {
                        const propertyArray = args[index];
                        const propertyValue = propertyArray[elementIndex];
                        if (type === PropertyModificationType.update) {
                            _set(newUiData, keyPath, propertyValue);
                        } else if (type === PropertyModificationType.remove) {
                            _set(newUiData, keyPath, undefined);
                        }
                    });
                    LinkageChangeHelper.checkAndHandleLinkageChange(newUiData, element);
                });
            });
            return result;
        };
        return descriptor;
    };
}
